import { useCallback, useEffect, useRef, useState } from "react";
import { pipelineService } from "../../../api/pipeline.service.js";

const BATCH_SIZE = 100;

export type MissingNewAxisIdsProgress = {
  updated: number;
  processingItems: number;
  testimonials: number;
  remaining: number | null;
  batch: number;
};

const EMPTY_PROGRESS: MissingNewAxisIdsProgress = {
  updated: 0,
  processingItems: 0,
  testimonials: 0,
  remaining: null,
  batch: 0,
};

export function useMissingNewAxisIdsFix(onComplete?: () => void) {
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<MissingNewAxisIdsProgress>(EMPTY_PROGRESS);
  const [error, setError] = useState<string | null>(null);
  const stopRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopRef.current = true;
    };
  }, []);

  const stop = useCallback(() => {
    stopRef.current = true;
  }, []);

  const start = useCallback(async () => {
    stopRef.current = false;
    setRunning(true);
    setError(null);
    setProgress(EMPTY_PROGRESS);

    let updated = 0;
    let processingItems = 0;
    let testimonials = 0;
    let batch = 0;

    try {
      while (!stopRef.current) {
        batch += 1;
        const result = await pipelineService.missingNewAxisIdsBatch({ batchSize: BATCH_SIZE });
        updated += result.updated;
        processingItems += result.processingItems;
        testimonials += result.testimonials;
        setProgress({ updated, processingItems, testimonials, remaining: result.remaining, batch });

        if (result.remaining <= 0) break;

        if (result.updated === 0) {
          setError(`Stopped: batch ${batch} wrote nothing while ${result.remaining} remain.`);
          break;
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Missing research ids batch failed");
    } finally {
      if (mountedRef.current) {
        setRunning(false);
        onComplete?.();
      }
    }
  }, [onComplete]);

  return { running, progress, error, start, stop };
}
